import { getInventoryFromVariations } from '../../inventory';
import { getCombinationsErrors, updateVariationsErrors } from '../../validations/variations';
import { shapeVariationsForApp } from '../../variations/shapeForApp';
import { reduceErrors } from '../../errors';
import { retain } from '../../variations/retain';
import {
  setBarcodeErrors,
  setCAPErrors,
  setCPIErrors,
  setPriceErrors,
  setQuantityErrors,
  setSKUErrors,
  setWeightErrors,
  updateProductErrorsFromVariations,
  updateProductVariationsErrors,
} from '../../product/errors';

import { ERROR, OVERSET, VALUE } from '../../../constants/product';
import { ETSY, SHOPIFY } from '../../../constants/channels';
import { OPERATIONS } from '../../../constants/bulkEdit';
import { FORM } from '../../../constants/forms';
import ATTRIBUTE from '../../../constants/attributes';

function isProfileOperation({ channel, operation }) {
  switch (channel) {
    case ETSY: {
      return operation.hasIn([VALUE, 'templateid']);
    }

    case SHOPIFY: {
      return operation.has('templateId');
    }

    default: {
      return false;
    }
  }
}

export default function variations({ actions, operation, product: source }) {
  let product = source;
  const channel = product.get('channel');

  switch (operation.get('type')) {
    case OPERATIONS[channel].VARIATIONS: {
      if (channel === ETSY && product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])) break;

      const isProfile = isProfileOperation({ channel, operation });
      let value = shapeVariationsForApp({
        category: operation.getIn([VALUE, 'taxonomyId']),
        channel,
        data: operation.get(VALUE),
      });

      if (isProfile) {
        value = retain({ channel, product, variations: value, validate: false });
      }

      value = updateVariationsErrors({ channel, variations: value });

      if (!reduceErrors(value.get(ERROR))) {
        product = updateProductVariationsErrors({
          channel,
          product: product.set(FORM.VARIATIONS, value),
        });
      }

      break;
    }

    case OPERATIONS[SHOPIFY].BARCODE.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setBarcodeErrors({ channel, product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.BARCODE])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.BARCODE]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].CAP.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.CAP, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setCAPErrors({ channel, product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.CAP])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.CAP]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].CPI.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.CPI, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setCPIErrors({ channel, product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.CPI])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.CPI]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].CHARGE_TAX.INLINE_EDIT:
    case OPERATIONS[SHOPIFY].CONTINUE_SELLING.INLINE_EDIT:
    case OPERATIONS[SHOPIFY].COUNTRY_CODE.INLINE_EDIT:
    case OPERATIONS[SHOPIFY].HS_CODE.INLINE_EDIT: {
      return getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });
    }

    case OPERATIONS[SHOPIFY].PHYSICAL.INLINE_EDIT:
    case OPERATIONS[SHOPIFY].WEIGHT.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setWeightErrors({ channel, product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.WEIGHT])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.WEIGHT]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].PRICE.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setPriceErrors({ channel, product });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.PRICE])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.PRICE]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].QUANTITY.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setQuantityErrors({ channel, product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.QUANTITY])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.QUANTITY]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].SKU.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setSKUErrors({ channel, product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.SKU])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.SKU]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    default: {
      break;
    }
  }

  return { actions, product };
}
